import { useEffect, useMemo, useState } from 'react'
import { LIFE_AREA_OPTIONS, lifeAreaLabel, type Choice } from '../utils/taskForm'

/**
 * The life areas to offer a person (owner 2026-09-05: "selectable and also
 * addable for any new life area not already present"): the canonical seven,
 * then the ones they have named on other tasks (most used first), then
 * anything already on this task or form that is not among them. Fails soft
 * to the canonical list when the server cannot be reached.
 */
export interface LifeAreaEntry { value: string; count: number; custom: boolean }

let cache: LifeAreaEntry[] | null = null
let inflight: Promise<LifeAreaEntry[]> | null = null

async function fetchLifeAreas(): Promise<LifeAreaEntry[]> {
  const r = await fetch('/api/hierarchy/life-areas', { credentials: 'include' })
  const body = await r.json().catch(() => ({}))
  if (!r.ok || body.success === false) throw new Error(body.error || 'Could not load the life areas')
  const areas = body?.data?.areas
  return Array.isArray(areas)
    ? areas.filter((a: unknown): a is LifeAreaEntry => Boolean(a) && typeof (a as LifeAreaEntry).value === 'string')
    : []
}

export function useLifeAreaOptions(extra: string[] = []): { options: Choice[]; remember: (value: string) => void } {
  const [known, setKnown] = useState<LifeAreaEntry[]>(cache || [])
  useEffect(() => {
    let alive = true
    if (!inflight) {
      inflight = fetchLifeAreas()
        .then((a) => { cache = a; return a })
        .catch(() => cache || [])
        .finally(() => { inflight = null })
    }
    void inflight.then((a) => { if (alive) setKnown(a) })
    return () => { alive = false }
  }, [])
  const extraKey = extra.filter(Boolean).join(' ')
  const options = useMemo(() => {
    const seen = new Set(LIFE_AREA_OPTIONS.map((o) => o.value))
    const out: Choice[] = [...LIFE_AREA_OPTIONS]
    const push = (v: string) => { if (v && !seen.has(v)) { seen.add(v); out.push({ value: v, label: lifeAreaLabel(v) }) } }
    for (const a of known) if (a.custom) push(a.value)
    for (const v of extraKey ? extraKey.split(' ') : []) push(v)
    return out
  }, [known, extraKey])
  // A just-saved area is offered on the next form without a round trip.
  const remember = (value: string) => {
    if (!value || LIFE_AREA_OPTIONS.some((o) => o.value === value)) return
    cache = [...(cache || []).filter((a) => a.value !== value), { value, count: 1, custom: true }]
    setKnown(cache)
  }
  return { options, remember }
}
